import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { request, requestVoid } from '@/shared/api'
import {
  type Client,
  ClientCardSchema,
  type ClientDraft,
  ClientListSchema,
  type ClientPatch,
  type ClientRegistration,
  ClientRegistrationSchema,
  ClientSchema,
  type RevokeResult,
  RevokeResultSchema,
} from '../model/schema'

export const clientsQueryKey = ['clients'] as const

export interface ClientsQuery {
  query?: string
  includeForgotten: boolean
  includeMerged: boolean
}

export function clientsQueryOptions(params: ClientsQuery) {
  const query = {
    q: params.query ?? '',
    include_forgotten: params.includeForgotten,
    include_merged: params.includeMerged,
    with_visits: true,
  }
  return queryOptions({
    queryKey: [...clientsQueryKey, query] as const,
    queryFn: (): Promise<Client[]> => request('/clients', ClientListSchema, { query }),
  })
}

export function clientQueryOptions(id: string) {
  return queryOptions({
    queryKey: [...clientsQueryKey, 'one', id] as const,
    queryFn: (): Promise<Client> => request(`/clients/${id}`, ClientCardSchema),
  })
}

/**
 * Заведение контакта из кабинета. Пару имя+телефон бэкенд сперва ищет среди
 * своих и отдаёт `matched`, если контакт нашёлся.
 *
 * Согласие не передаём: его дословную формулировку знает публичная форма, а со
 * слов оно фиксируется при записи на выход — сочинять текст журнала здесь
 * нельзя, журнал доказывает, что человек видел именно это.
 */
export function useRegisterClientMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (draft: ClientDraft): Promise<ClientRegistration> =>
      request('/clients', ClientRegistrationSchema, { method: 'POST', body: draft }),
    onSettled: () => qc.invalidateQueries({ queryKey: clientsQueryKey }),
  })
}

export function useUpdateClientMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ClientPatch }) =>
      request(`/clients/${id}`, ClientSchema, { method: 'PATCH', body: patch }),
    onSettled: () => qc.invalidateQueries({ queryKey: clientsQueryKey }),
  })
}

/** Объединение всегда вручную: автоматике решать, кто дубль, нельзя. */
export function useMergeClientsMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ sourceId, targetId }: { sourceId: string; targetId: string }) =>
      request('/clients/merge', ClientSchema, {
        method: 'POST',
        body: { source_client_id: sourceId, target_client_id: targetId },
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: clientsQueryKey }),
  })
}

/** Обезличивание, необратимо. Строка остаётся ради прошлых записей. */
export function useForgetClientMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string): Promise<void> =>
      requestVoid(`/clients/${id}/forget`, { method: 'POST' }),
    onSettled: () => qc.invalidateQueries({ queryKey: clientsQueryKey }),
  })
}

export function useRevokeConsentMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string): Promise<RevokeResult> =>
      request(`/clients/${id}/consent/revoke`, RevokeResultSchema, {
        method: 'POST',
        body: { purpose: 'booking' },
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: clientsQueryKey }),
  })
}
